import { forwardRef } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { cn } from '@/utils/cn';

/**
 * Checkbox Component
 *
 * Checkbox input using Radix UI
 */

interface CheckboxProps {
  label?: string;
  error?: string;
  disabled?: boolean;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  className?: string;
  id?: string;
  /** When true, checkbox doesn't expand to full width (useful for inline layouts) */
  inline?: boolean;
  /** 'md' (default) = 20px box; 'sm' = 14px compact box. Use this instead of className size overrides (cn does not merge Tailwind conflicts). */
  size?: 'sm' | 'md';
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(
  (
    {
      label,
      error,
      disabled,
      checked,
      onCheckedChange,
      className = '',
      id,
      inline = false,
      size = 'md',
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
    },
    ref
  ) => {
    const isSm = size === 'sm';
    return (
      <div className={label && !inline ? 'w-full' : 'w-auto'}>
        <div className="flex items-center gap-2">
          <CheckboxPrimitive.Root
            ref={ref}
            id={id}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            checked={checked}
            onCheckedChange={onCheckedChange}
            disabled={disabled}
            className={cn(
              isSm ? 'w-3.5 h-3.5 border rounded-[3px]' : 'w-5 h-5 border-2 rounded',
              'border-gray-300',
              'focus:outline-none',
              'transition-all duration-200',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'data-[state=checked]:bg-bs-secondary data-[state=checked]:border-bs-secondary',
              'flex items-center justify-center',
              className
            )}
          >
            <CheckboxPrimitive.Indicator>
              <svg
                width={isSm ? 10 : 12}
                height={isSm ? 10 : 12}
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-secondary-contrast"
              >
                <path
                  d="M2 6L5 9L10 3"
                  stroke="currentColor"
                  strokeWidth={isSm ? 1.75 : 2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </CheckboxPrimitive.Indicator>
          </CheckboxPrimitive.Root>

          {label && (
            <label
              htmlFor={id}
              className={cn(
                'text-sm text-gray-700 cursor-pointer select-none',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              {label}
            </label>
          )}
        </div>

        {error && (
          <p className="mt-1 text-xs text-danger" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
